import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import PhotoUpload from '../components/PhotoUpload';
import DateRestriction from '../components/DateRestriction';
import { supabase } from '../lib/supabase';
import { motion } from 'framer-motion';
import { Camera } from 'lucide-react';
import { APP_SETTINGS_ID } from '../lib/constants';

interface Photo {
  id: string;
  image_url: string;
  caption?: string;
  uploaded_by: string;
  created_at: string;
}

const PhotosPage: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [unlockDate, setUnlockDate] = useState<Date>(new Date('2025-08-02T16:00:00'));

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch unlock date
        const { data: settings } = await supabase
          .from('app_settings')
          .select('photo_album_unlock_date')
          .eq('id', APP_SETTINGS_ID)
          .single();

        if (settings?.photo_album_unlock_date) {
          setUnlockDate(new Date(settings.photo_album_unlock_date));
        }

        // Fetch photos
        const { data: photosData, error } = await supabase
          .from('photos')
          .select('*')
          .order('created_at', { ascending: false });
          
        if (error) throw error;
        
        setPhotos(photosData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);

  const handleUpload = async (file: File, caption: string, uploader: string) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random().toString(36).substring(2)}.${fileExt}`;
      const filePath = `photos/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('photos')
        .upload(filePath, file);
        
      if (uploadError) throw uploadError;
      
      const { data: { publicUrl } } = supabase.storage
        .from('photos')
        .getPublicUrl(filePath);
      
      const { error: insertError } = await supabase
        .from('photos')
        .insert({
          image_url: publicUrl,
          caption: caption || null,
          uploaded_by: uploader
        });
        
      if (insertError) throw insertError;
      
      const newPhoto: Photo = {
        id: Math.random().toString(36).substring(2),
        image_url: publicUrl,
        caption: caption || undefined,
        uploaded_by: uploader,
        created_at: new Date().toISOString()
      };
      
      setPhotos([newPhoto, ...photos]);
      setShowUploadModal(false);
    } catch (error) {
      console.error('Error uploading photo:', error);
      throw error;
    }
  };

  return (
    <DateRestriction targetDate={unlockDate}>
      <div className="min-h-screen py-12 px-4">
        <PageHeader 
          title="Álbum de Fotos" 
          subtitle="Compartilhe conosco os momentos especiais do nosso casamento"
        />
        
        <div className="container mx-auto max-w-4xl mb-10">
          <div className="bg-wedding-beige-100 p-6 rounded-lg text-center mb-8">
            <p className="text-wedding-blue-800 mb-4">
              Tire fotos durante nosso casamento e compartilhe aqui para que todos possam ver!
            </p>
          </div>
          
          <button
            onClick={() => setShowUploadModal(true)}
            className="w-full py-4 bg-wedding-blue-600 text-white rounded-lg hover:bg-wedding-blue-700 transition-colors flex items-center justify-center gap-2"
          >
            <Camera size={20} />
            Compartilhar Foto
          </button>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
          </div>
        ) : (
          <div className="container mx-auto max-w-6xl">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((photo, index) => (
                <motion.div
                  key={photo.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <div className="h-64 overflow-hidden">
                    <img 
                      src={photo.image_url} 
                      alt={photo.caption || 'Foto do casamento'} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="p-4">
                    {photo.caption && (
                      <p className="text-gray-700 mb-2">{photo.caption}</p>
                    )}
                    
                    <div className="flex justify-between items-center text-sm text-gray-500">
                      <span>Por: {photo.uploaded_by}</span>
                      <span>{new Date(photo.created_at).toLocaleDateString('pt-BR')}</span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        )}
        
        <PhotoUpload
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onUpload={handleUpload}
        />
      </div>
    </DateRestriction>
  );
};

export default PhotosPage;